import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  FormControlLabel,
  Tooltip,
  Grid,
  Chip,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import { RootState } from "../../store/Store";
import {
  fetchData,
  postData,
  putData,
  deleteData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import { setIsLoading } from "../../store/LoadingSlice";
import {
  processComplianceTemplatesUrl,
} from "../../Service/Apis";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import ConfirmDialog from "../../components/Dialogs/ConfirmDialog";
import PageTitle from "../../components/Page Component/PageTitle";
import { getDefaultQuestions, formatDateToDDMMYYYY } from "./utils";
import { getDataGridPaginationProps } from "utils/paginationConfig";

// Updated interfaces for the enhanced structure

interface Question {
  id: string;
  question: string;
  category?: string; // Renamed from department for frontend
}

interface Category {  // Renamed from Department
  id: string;
  name: string;
  percentage: number; // Weight percentage for this category
}

interface TemplateData {
  questions: Question[];
  categories: Category[]; // (Add departments array) Renamed from departments
  departments?: Category[]; // done for Backward compatibility
  // Removed: sprintCount, sprintNames (moved to assignments)
}

interface Template {
  template_id: string;
  template_name: string;
  template_data: TemplateData;
  is_active: boolean;
  created_on: string;
  updated_on: string;
}

interface NotificationResponse {
  success: boolean;
  notifications_sent: number;
  message?: string;
}

const ProcessComplianceTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [formData, setFormData] = useState<Partial<Template>>({
    template_name: "",
    is_active: true,
    template_data: {
      questions: [],
      categories: [], // Renamed from departments
      // Removed: sprintCount, sprintNames
    },
  });

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await dispatch2(
        fetchData(`${processComplianceTemplatesUrl}?role=${selectedRole}`)
      );
      setTemplates(response as Template[]);
    } catch (error) {
      console.error("Error fetching templates:", error);
      showToast("Error fetching templates", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleOpenDialog = (template?: Template) => {
    if (template) {
      setEditMode(true);
      setSelectedTemplate(template);

      setFormData({
        template_name: template.template_name,
        is_active: template.is_active,
        template_data: {
          ...JSON.parse(JSON.stringify(template.template_data)), // Deep copy
          categories: template.template_data.categories || template.template_data.departments || [],
        },
      });
    } else {
      setEditMode(false);
      setSelectedTemplate(null);

      // Initialize with default questions
      setFormData({
        template_name: "",
        is_active: true,
        template_data: {
          questions: getDefaultQuestions(),
          categories: getDefaultCategories(), // Renamed from departments
        },
      });
    }
    setOpenDialog(true);
  };
  const getDefaultCategories = (): Category[] => {  // Renamed from getDefaultDepartments
    return [
      { id: "cat-1", name: "Sprint Planning", percentage: 20 },
      { id: "cat-2", name: "Development & Testing", percentage: 70 },
      { id: "cat-3", name: "Deployment", percentage: 9 },
      { id: "cat-4", name: "Review and Retrospective", percentage: 1 },
    ];
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleAddCategory = () => {  // Renamed from handleAddDepartment
    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        categories: [  // Renamed from departments
          ...formData.template_data!.categories,
          { id: `cat-${Date.now().toString()}`, name: "", percentage: 0 },
        ],
      },
    });
  };

  const handleUpdateCategory = (  // Renamed from handleUpdateDepartment
    index: number,
    field: "name" | "percentage",
    value: string | number
  ) => {
    const updatedCategories = [...formData.template_data!.categories];  // Renamed from departments
    updatedCategories[index] = {
      ...updatedCategories[index],
      [field]: value,
    };

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        categories: updatedCategories,  // Renamed from departments
      },
    });
  };

  const handleRemoveCategory = (index: number) => {  // Renamed from handleRemoveDepartment
    const updatedCategories = [...formData.template_data!.categories];  // Renamed from departments
    const categoryId = updatedCategories[index].id;
    updatedCategories.splice(index, 1);

    // Update questions that belonged to this category
    const updatedQuestions = formData.template_data!.questions.map((q) => {
      if (q.category === categoryId) {  // Renamed from department
        return { ...q, category: undefined };  // Renamed from department
      }
      return q;
    });

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        categories: updatedCategories,  // Renamed from departments
        questions: updatedQuestions,
      },
    });
  };

  const handleAssignQuestionCategory = (  // Renamed from handleAssignQuestionDepartment
    questionIndex: number,
    categoryId: string  // Renamed from departmentId
  ) => {
    const updatedQuestions = [...formData.template_data!.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      category: categoryId,  // Renamed from department
    };

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: updatedQuestions,
      },
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;

    if (name === "is_active") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleAddQuestion = () => {
    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: [
          ...formData.template_data!.questions,
          { id: `q-${Date.now().toString()}`, question: "" },
        ],
      },
    });
  };

  const handleUpdateQuestion = (index: number, value: string) => {
    const updatedQuestions = [...formData.template_data!.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      question: value,
    };

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: updatedQuestions,
      },
    });
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...formData.template_data!.questions];
    updatedQuestions.splice(index, 1);

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: updatedQuestions,
      },
    });
  };

  // Sprints are now handled in assignments

  // Add this validation check in the handleSubmit function
  const handleSubmit = async () => {
    try {
      dispatch(setIsLoading(true));

      if (!formData.template_name) {
        showToast("Template name is required", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      if (
        !formData.template_data?.questions ||
        formData.template_data?.questions.length === 0
      ) {
        showToast("At least one question is required", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      // Check for empty questions
      const emptyQuestions = formData.template_data?.questions.some(
        (q) => !q.question.trim()
      );
      if (emptyQuestions) {
        showToast("Questions cannot be empty", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      // Check all questions are assigned to a category
      const uncategorizedQuestions = formData.template_data?.questions.filter(
        (q) => !q.category
      ) ?? [];
      if (uncategorizedQuestions.length > 0) {
        showToast(
          "Kindly assign all unassigned questions to an existing category",
          "error",
          true
        );
        dispatch(setIsLoading(false));
        return;
      }

      // Check category percentages total 100%
      const totalPercentage = formData.template_data?.categories.reduce(  // Renamed from departments
        (sum, cat) => sum + (cat.percentage || 0),  // Renamed from departmentt
        0
      );
      if (totalPercentage !== 100) {
        showToast(
          "Category percentages must total exactly 100%",  // Renamed from Department
          "error",
          true
        );
        dispatch(setIsLoading(false));
        return;
      }

      // Remove sprint validation - no longer needed

      if (editMode && selectedTemplate) {
        await dispatch2(
          putData(
            `${processComplianceTemplatesUrl}${selectedTemplate.template_id}/?role=${selectedRole}`,
            formData
          )
        );
        showToast("Template updated successfully", "success", true);
      } else {
        await dispatch2(
          postData(
            `${processComplianceTemplatesUrl}?role=${selectedRole}`,
            formData
          )
        );
        showToast("Template created successfully", "success", true);
      }

      handleCloseDialog();
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      showToast("Error saving template", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // Add state variables for the delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  // Replace your delete handler with this
  const handleDeleteTemplate = (templateId: string) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  const executeTemplateDelete = async () => {
    if (!templateToDelete) return;

    try {
      dispatch(setIsLoading(true));
      await dispatch2(
        deleteData(
          `${processComplianceTemplatesUrl}${templateToDelete}/?role=${selectedRole}`
        )
      );
      showToast("Template deleted successfully", "success", true);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      showToast("Error deleting template", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // Add the dialog to your render function
  const renderDeleteConfirmDialog = () => (
    <ConfirmDialog
      open={deleteDialogOpen}
      title="Delete Template"
      message="Are you sure you want to delete this template? This action cannot be undone and will remove all associated data."
      severity="error"
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      onConfirm={() => {
        setDeleteDialogOpen(false);
        executeTemplateDelete();
      }}
      onCancel={() => {
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
      }}
    />
  );

  const toggleTemplateStatus = async (template: Template) => {
    try {
      dispatch(setIsLoading(true));
      await dispatch2(
        putData(
          `${processComplianceTemplatesUrl}${template.template_id}/?role=${selectedRole}`,
          {
            is_active: !template.is_active,
          }
        )
      );
      showToast(
        `Template ${
          template.is_active ? "deactivated" : "activated"
        } successfully`,
        "success",
        true
      );
      fetchTemplates();
    } catch (error) {
      console.error("Error toggling template status:", error);
      showToast("Error updating template status", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // Default questions - these are the fixed guidelines

  const columns: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr No.",
      width: 100,
      valueGetter: (params) => {
        const index = templates.findIndex(
          (t) => t.template_id === params.row.template_id
        );
        return index + 1;
      },renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Sr No.</strong>)
    },
    {
      field: "template_name",
      headerName: "Template Name",
      flex: 1,
      renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Template Name </strong>)
    },
    {
      field: "questions_count",
      headerName: "Questions",
      width: 120,
      valueGetter: (params) => params.row.template_data.questions?.length || 0,
      renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Questions</strong>)
    },
    {
      field: "categories_count",
      headerName: "Categories",
      width: 120,
      valueGetter: (params) => {
        // Handle both 'categories' and 'departments' for backward compatibility
        const categories = params.row.template_data.categories || params.row.template_data.departments || [];
        return categories.length;
      },
      renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Categories</strong>)
    },
    {
      field: "created_on",
      headerName: "Created On",
      width: 180,
      valueFormatter: (params) => {
        return params.value ? formatDateToDDMMYYYY(new Date(params.value)) : "—";
      },renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Created On</strong>)
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Inactive"}
          size="small"
          color={params.row.is_active ? "success" : "default"}
        />
      ),renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Status</strong>)
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteTemplate(params.row.template_id)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Actions</strong>)
    },
  ];

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <PageTitle>Process Compliance Templates</PageTitle>
            <Box>
              {(selectedRole === "HR" || selectedRole === "Admin") && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Template
                </Button>
              )}
            </Box>
          </Box>

          <DataGrid
            rows={templates}
            columns={columns}
            getRowId={(row) => row.template_id}
            autoHeight
            {...getDataGridPaginationProps()}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        </Paper>
      </Container>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "80vh" },
        }}
      >
        <DialogTitle>
          {editMode
            ? "Edit Process Compliance Template"
            : "Create Process Compliance Template"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              name="template_name"
              label="Template Name"
              value={formData.template_name}
              onChange={handleFormChange}
              fullWidth
              required
            />

            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleFormChange}
                />
              }
              label="Active"
            />

            {/* Category configuration */}
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="h6">Categories</Typography>
                <Button
                  variant="outlined"
                  onClick={handleAddCategory}
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Add Category
                </Button>
              </Box>

              <Typography variant="body2" color="black" gutterBottom>
                Define categories and their percentage weights (must total
                100%)
              </Typography>

              {formData.template_data?.categories.map((cat, index) => (
                <Box
                  key={cat.id}
                  display="flex"
                  gap={2}
                  mb={3}
                  alignItems="center"
                >
                  <TextField
                    label="Category Name"
                    value={cat.name}
                    onChange={(e) =>
                      handleUpdateCategory(index, "name", e.target.value)
                    }
                    size="small"
                    sx={{ flexGrow: 2 }}
                  />
                  <TextField
                    label="Weight %"
                    value={cat.percentage}
                    onChange={(e) =>
                      handleUpdateCategory(
                        index,
                        "percentage",
                        Number(e.target.value)
                      )
                    }
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: "100px" }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveCategory(index)}
                    size="small"
                    disabled={formData.template_data!.categories.length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              {/* Weight total indicator */}
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography variant="body2">
                  Total weight:
                  <strong>
                    {formData.template_data?.categories.reduce(
                      (sum, cat) => sum + (cat.percentage || 0),
                      0
                    )}
                    %
                  </strong>
                </Typography>
                {formData.template_data?.categories.reduce(
                  (sum, cat) => sum + (cat.percentage || 0),
                  0
                ) !== 100 && (
                  <Chip
                    label="Total must be 100%"
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            </Box>
            {/* Questions section */}
            <Box sx={{ mt: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="h6">
                  Compliance Guidelines/Questions
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleAddQuestion}
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Add Question
                </Button>
              </Box>

              {/* Group questions by category */}
              {formData.template_data?.categories.map((cat) => (
                <Box key={cat.id} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      pb: 1,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ flexGrow: 1 }}
                    >
                      {cat.name} ({cat.percentage}%)
                    </Typography>
                  </Box>

                  {/* Questions for this category */}
                  {formData.template_data?.questions
                    .filter((q) => q.category === cat.id)
                    .map((question, globalIndex) => {
                      // Find the actual index in the full array
                      const actualIndex =
                        formData.template_data!.questions.findIndex(
                          (q) => q.id === question.id
                        );
                      return (
                        <Box
                          key={question.id}
                          display="flex"
                          gap={1}
                          mb={1}
                          alignItems="center"
                        >
                          <TextField
                            label={`Question`}
                            value={question.question}
                            onChange={(e) =>
                              handleUpdateQuestion(actualIndex, e.target.value)
                            }
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                          />
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={question.category || ""}
                              onChange={(e) =>
                                handleAssignQuestionCategory(
                                  actualIndex,
                                  e.target.value
                                )
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em></em>
                              </MenuItem>
                              {formData.template_data?.categories.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveQuestion(actualIndex)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      );
                    })}

                  {/* Button to add question directly to this category */}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newQuestion = {
                        id: `q-${Date.now().toString()}`,
                        question: "",
                        category: cat.id,
                      };
                      setFormData({
                        ...formData,
                        template_data: {
                          ...formData.template_data!,
                          questions: [
                            ...formData.template_data!.questions,
                            newQuestion,
                          ],
                        },
                      });
                    }}
                    sx={{ mt: 1 }}
                  >
                    Add Question to {cat.name}
                  </Button>
                </Box>
              ))}

              {/* Questions without a category */}
              {(formData.template_data?.questions || []).filter(
                (q) => !q.category
              ).length > 0 && (
                <Box
                  sx={{
                    mb: 3,
                    border: "2px solid",
                    borderColor: "error.main",
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      pb: 1,
                      borderBottom: "1px solid #e0e0e0",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="error"
                      sx={{ flexGrow: 1 }}
                    >
                      Unassigned Questions
                    </Typography>
                    <Chip label="Assign to a category or delete them" color="error" size="small" />
                  </Box>

                  {formData.template_data?.questions
                    .filter((q) => !q.category)
                    .map((question, idx) => {
                      const actualIndex =
                        formData.template_data!.questions.findIndex(
                          (q) => q.id === question.id
                        );
                      return (
                        <Box
                          key={question.id}
                          display="flex"
                          gap={1}
                          mb={1}
                          alignItems="center"
                        >
                          <TextField
                            label={`Question`}
                            value={question.question}
                            onChange={(e) =>
                              handleUpdateQuestion(actualIndex, e.target.value)
                            }
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            error
                          />
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel id={`category-label-${question.id}`}>Category</InputLabel>
                            <Select
                              labelId={`category-label-${question.id}`}
                              value={question.category || ""}
                              onChange={(e) =>
                                handleAssignQuestionCategory(
                                  actualIndex,
                                  e.target.value
                                )
                              }
                              displayEmpty
                              label="Category"
                            >
                              <MenuItem value="">
                                <em></em>
                              </MenuItem>
                              {formData.template_data?.categories.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveQuestion(actualIndex)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      );
                    })}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      {renderDeleteConfirmDialog()}
    </>
  );
};

export default ProcessComplianceTemplates;
